import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
    Alert, Box, Button, ColumnLayout, FormField, Select, SelectProps, SpaceBetween, StatusIndicator,
} from '@amzn/awsui-components-react';
import CONSTANTS from 'src/utils/constants';
import ErrorUtils from 'src/utils/ErrorUtils';
import { ColumnDataType, ColumnDef, ColumnSourceType, TableType } from 'src/models/segment-cdt/SegmentCdtTypes';
import { DerivedAllocationSheetBuilderContext } from 'src/services/derived-allocation-sheet/DerivedAllocationSheetBuilderContext';
import { ACTIONS as DAS_ACTIONS } from 'src/services/derived-allocation-sheet/DerivedAllocationSheetBuilderReducer';
import DasMappingPreview from './DasMappingPreview';
import DasAllocationOutput, { outputKeyOf } from './DasAllocationOutput';
import DasStepInfo from './DasStepInfo';

export const SAVE_RETURNED_NO_SHEET_MESSAGE = 'The segment mapping was saved, but the response did not include the sheet. Refresh the page to see it.';
export const NO_GROUPS_MESSAGE = 'Add at least one allocation group in Step 3 before configuring the segment mapping.';

/** True for a column that can hold allocation group names: the backend accepts manual text columns only. */
export const canHoldGroupNames = (column: ColumnDef) =>
    column.sourceType === ColumnSourceType.MANUAL && column.dataType === ColumnDataType.TEXT;

/**
 * Step 4 (Segment to group mapping) of the derived allocation sheet builder. The user picks the segment data
 * table that maps each segment to an allocation group, and the manual text column of that table that holds
 * the Step 3 group names; Save stores both on the draft through Set Mapping, which also sets the sheet's
 * segment type from the table. The table's other columns are listed but cannot be picked. While the picked
 * mapping differs from the saved one, the step reports unsaved work, so the version history waits for it. Once
 * a manual text column is picked, a read-only preview lists the table's segments with the group each one names.
 * Under it, the allocation by segment shows what the last run wrote; once there is one, the preview shows only
 * while other picks are tried.
 */
export default function DasMappingSection() {
    const { dasState, dasDispatch, services, sheetId } = useContext(DerivedAllocationSheetBuilderContext);
    // The builder renders the steps only once a sheet is loaded.
    const sheet = dasState.sheet!;
    const editable = dasState.viewMode === CONSTANTS.VIEW_MODE.EDITABLE;
    // The sheet response does not carry its group ids, so the groups are read here; every write to the sheet
    // (a group added or deleted in Step 3 included) reads them again.
    const [groupsResult, , groupsError] = services.derivedAllocationSheetService.getDASAllocationGroups(
        `fetch-${dasState.sheetRefreshToken}`, sheetId, sheet.sheetVersion);
    const hasGroups = (groupsResult?.allocationGroups ?? []).length > 0;
    // Unset until the groups arrive, so the preview does not flag every name as unknown meanwhile.
    const groupNames = useMemo(() => groupsResult?.allocationGroups?.map((g) => g.description), [groupsResult]);
    const [tableId, setTableId] = useState(sheet.mappingSegmentCdtId);
    const [columnId, setColumnId] = useState(sheet.mappingColumnId);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(undefined as string | undefined);
    const [saved, setSaved] = useState(false);
    const mountedRef = useRef(true);
    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    // A new saved mapping (after a save, or another version opened) replaces the picks.
    useEffect(() => {
        setTableId(sheet.mappingSegmentCdtId);
        setColumnId(sheet.mappingColumnId);
    }, [sheet.mappingSegmentCdtId, sheet.mappingColumnId]);

    const listRequest = useMemo(() => ({ tableType: TableType.SEGMENT_LEVEL }), []);
    const [tablesResult, tablesLoading, tablesError] = services.segmentDataTablesService.listSegmentDataTables(listRequest);
    const [detail, detailLoading, detailError] = services.segmentDataTablesService.getSegmentDataTable(tableId);

    const tableOptions: SelectProps.Option[] = useMemo(() => (tablesResult?.tables ?? [])
        .filter((t) => !!t.tableId)
        .map((t) => ({ value: t.tableId, label: t.tableName || t.tableId, description: t.segmentType })), [tablesResult]);
    // The definition of an earlier pick can still be on screen while the new one loads.
    const columns = detail?.table?.tableId === tableId ? detail?.table?.columns ?? [] : [];
    const columnOptions: SelectProps.Option[] = columns
        .filter((c) => !!c.columnId)
        .map((c) => ({
            value: c.columnId,
            label: c.displayName || c.columnId,
            disabled: !canHoldGroupNames(c),
            labelTag: canHoldGroupNames(c) ? undefined : 'not a manual text column',
        }));
    const pickedColumn = columns.find((c) => c.columnId === columnId);

    const dirty = tableId !== sheet.mappingSegmentCdtId || columnId !== sheet.mappingColumnId;
    // Once a run has allocated the saved mapping, its allocation below shows the result, so the preview is only
    // needed before a run or while other picks are being tried.
    const showPreview = !outputKeyOf(sheet) || dirty;
    const canSave = editable && hasGroups && !saving && dirty && !!tableId && !!pickedColumn && canHoldGroupNames(pickedColumn);

    useEffect(() => {
        dasDispatch(DAS_ACTIONS.SET_STEP_UNSAVED.withPayload({ step: 4, unsaved: editable && (dirty || saving), saving }));
    }, [editable, dirty, saving]);
    useEffect(() => () => dasDispatch(DAS_ACTIONS.SET_STEP_UNSAVED.withPayload({ step: 4, unsaved: false })), []);

    const pickTable = (id?: string) => {
        setTableId(id);
        setColumnId(undefined);
        setSaved(false);
        setSaveError(undefined);
    };

    const pickColumn = (id?: string) => {
        setColumnId(id);
        setSaved(false);
        setSaveError(undefined);
    };

    /** Saves the picked table and column; the backend refuses a column that is not manual text in the open period. */
    const save = async () => {
        if (!canSave || !tableId || !columnId) return;
        setSaving(true);
        setSaveError(undefined);
        try {
            const { sheetData } = await services.derivedAllocationSheetService.setDerivedAllocationSheetMapping({
                sheetId, mappingSegmentCdtId: tableId, mappingColumnId: columnId,
            });
            if (!mountedRef.current) return;
            if (sheetData == null) {
                setSaveError(SAVE_RETURNED_NO_SHEET_MESSAGE);
                return;
            }
            setSaved(true);
            dasDispatch(DAS_ACTIONS.REFRESH_SHEET);
        } catch (ex) {
            if (mountedRef.current) setSaveError(ErrorUtils.getMessage(ex));
        } finally {
            if (mountedRef.current) setSaving(false);
        }
    };

    // A saved pick shows its id only once the table list or the columns have loaded without it.
    const selectedTable = tableOptions.find((o) => o.value === tableId)
        ?? (tableId ? { value: tableId, label: tablesLoading ? 'Loading...' : tableId } : null);
    const selectedColumn = columnOptions.find((o) => o.value === columnId)
        ?? (columnId ? { value: columnId, label: detailLoading ? 'Loading...' : columnId } : null);
    const loadError = tablesError || detailError;

    return (
        <div className="wizardBoxContentContainer" data-class="dasStep4">
            <SpaceBetween direction="vertical" size="m">
                <DasStepInfo>
                    Pick the segment data table that maps each segment to an allocation group, and its manual text column
                    that holds the Step 3 group names. Execute reads the mapping from the open period.
                </DasStepInfo>
                {editable && groupsResult != null && !hasGroups && <Alert type="info">{NO_GROUPS_MESSAGE}</Alert>}
                {!!groupsError && <Alert type="error" header="Unable to load allocation groups">{groupsError}</Alert>}
                {!!loadError && <Alert type="error" header="Unable to load segment data tables">{loadError}</Alert>}
                {!!saveError && <Alert type="error" header="Segment mapping not saved">{saveError}</Alert>}
                <ColumnLayout columns={2}>
                    <FormField label="Segment data table" description="A segment-level table with a row for each segment">
                        <Select data-class="dasMappingTable" selectedOption={selectedTable} options={tableOptions} filteringType="auto"
                                placeholder="Select a segment data table" empty="No segment data tables"
                                statusType={tablesLoading ? 'loading' : 'finished'} loadingText="Loading segment data tables"
                                disabled={!editable || saving}
                                onChange={({ detail: d }) => pickTable(d.selectedOption.value)} />
                    </FormField>
                    <FormField label="Group name column" description="A manual text column of the table">
                        <Select data-class="dasMappingColumn" selectedOption={selectedColumn} options={columnOptions}
                                placeholder={tableId ? 'Select the column with the group names' : 'Select a table first'}
                                empty="The table has no columns"
                                statusType={detailLoading ? 'loading' : 'finished'} loadingText="Loading columns"
                                disabled={!editable || saving || !tableId}
                                onChange={({ detail: d }) => pickColumn(d.selectedOption.value)} />
                    </FormField>
                </ColumnLayout>
                {/* Without the Step 3 groups the statuses mean nothing; the groups alert above says why. */}
                {showPreview && !groupsError && !!tableId && !!columnId && !!pickedColumn && canHoldGroupNames(pickedColumn) && (
                    <DasMappingPreview tableId={tableId} columnId={columnId} groupNames={groupNames} />
                )}
                <Box data-class="dasMappingSegmentType">
                    <b>Segment type: </b>{sheet.segmentType || 'Set from the table when the mapping is saved'}
                </Box>
                {editable && (
                    <div className="actionButtonsContainer">
                        <Box float="left">
                            {saved && !dirty && <StatusIndicator type="success">Segment mapping saved.</StatusIndicator>}
                        </Box>
                        <Box float="right">
                            <Button data-class="saveButton" variant="primary" disabled={!canSave} onClick={save}>
                                {saving ? 'Saving...' : 'Save mapping'}
                            </Button>
                        </Box>
                    </div>
                )}
                <DasAllocationOutput />
            </SpaceBetween>
        </div>
    );
}
